<template>
  <div v-if="visible" class="jackpot-container">
    <audio ref="coinAudio" :src="coinAudioMp3" autoplay loop></audio>

    <!-- Monedas izquierda -->
    <img
      v-for="n in 10"
      :key="'l' + n"
      class="coin left-coin"
      :class="`${coinAnimation}-left`"
      src="@/assets/images/coin.svg"
      :style="{ top: `${randomTop()}%`, animationDelay: `${n * 0.2}s` }"
    />

    <!-- Monedas derecha -->
    <img
      v-for="n in 10"
      :key="'r' + n"
      class="coin right-coin"
      :class="`${coinAnimation}-right`"
      src="@/assets/images/coin.svg"
      :style="{ top: `${randomTop()}%`, animationDelay: `${n * 0.2}s` }"
    />

    <div class="jackpot-box">
      <h1 class="jackpot-text">¡Felicidades!</h1>
      <p class="jackpot-text-description">
        <b>{{ winner.name }}</b>
      </p>
      <p class="jackpot-text-description">
        Has ganado <b>{{ wonAmount }}</b> el premio del Jackpot.
      </p>
      <div class="jackpot-amount">{{ formatMoney(animatedAmount) }}</div>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from "lodash";
import { nextTick } from "vue";
import { mapGetters } from "vuex";
import accounting from "accounting";
import coinAudioMp3 from "@/assets/audio/coin-sound.mp3";

export default {
  name: "WinnerAlertComponent",
  props: {
    winner: { type: Object, required: true },
    coinAnimation: { type: String, default: "fly" }, // fly | bounce | spin
  },
  data() {
    return {
      animatedAmount: 0,
      visible: true,
      coinAudioMp3,
    };
  },
  emits: ["close"],
  mounted() {
    this.startAnimation();
  },
  computed: {
    ...mapGetters("clientAuth", ["getLoggedUser"]),
    wonAmount() {
      return accounting.formatMoney(
        this.winner.amount,
        this.winner.currency || "RD$",
        0,
        ",",
        "."
      );
    },
  },
  methods: {
    formatMoney(value) {
      return accounting.formatMoney(
        value,
        this.winner.currency || "RD$",
        0,
        ",",
        "."
      );
    },
    startAnimation() {
      const { amount = 0 } = cloneDeep(this.winner);
      const { balance = 0 } = cloneDeep(this.getLoggedUser) || {};
      if (this.$refs.coinAudio) {
        this.$refs.coinAudio.volume = 0.5;
        this.$refs.coinAudio.currentTime = 0;
        this.$refs.coinAudio.play().catch(() => {});
      }

      let current = balance || 0;
      this.animatedAmount = current;
      const prize = amount + current;
      const increment = Math.ceil(amount / 100);
      const interval = setInterval(() => {
        if (current >= prize) {
          this.animatedAmount = prize;
          clearInterval(interval);
        } else {
          current += increment;
          this.animatedAmount = current;
        }
      }, 20);

      setTimeout(() => {
        this.visible = false;
        if (this.$refs.coinAudio) {
          this.$refs.coinAudio.pause();
          this.$refs.coinAudio.currentTime = 0;
        }
        nextTick(() => {
          this.$emit("close");
        });
      }, 6000);
    },
    randomTop() {
      return Math.floor(Math.random() * 80);
    },
  },
};
</script>

<style lang="scss" scoped>
.jackpot-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(10, 10, 10, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.jackpot-box {
  background: #ffd700;
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  animation: pop 0.6s ease-out;
  box-shadow: 0 0 20px #fff8dc;
}

.jackpot-text {
  font-size: 2em;
  font-weight: bold;
  color: #b30000;
  margin-bottom: 10px;
}

.jackpot-text-description {
  font-size: 1.2em;
  color: #333;
  margin-bottom: 20px;
}

.jackpot-amount {
  font-size: 2.5em;
  font-weight: bold;
  color: #222;
}

.coin {
  position: absolute;
  width: 40px;
  height: 40px;
  opacity: 0;
}

.left-coin {
  left: -60px;
}

.right-coin {
  right: -60px;
}

// ========== ANIMACIONES ==========
.fly-left {
  animation: fly-left 2s ease-in-out forwards;
}

.fly-right {
  animation: fly-right 2s ease-in-out forwards;
}

.bounce-left {
  animation: bounce-left 2s ease-in-out forwards;
}

.bounce-right {
  animation: bounce-right 2s ease-in-out forwards;
}

.spin-left {
  animation: spin-left 2s ease-in-out forwards;
}

.spin-right {
  animation: spin-right 2s ease-in-out forwards;
}

// ========== KEYFRAMES ==========
@keyframes fly-left {
  0% {
    transform: translateX(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateX(500px) rotate(720deg);
    opacity: 0;
  }
}

@keyframes fly-right {
  0% {
    transform: translateX(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateX(-500px) rotate(720deg);
    opacity: 0;
  }
}

@keyframes bounce-left {
  0% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  50% {
    transform: translateY(-80px) scale(1.2);
  }
  100% {
    transform: translateX(500px) translateY(150px) scale(0.5);
    opacity: 0;
  }
}

@keyframes bounce-right {
  0% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  50% {
    transform: translateY(-80px) scale(1.2);
  }
  100% {
    transform: translateX(-500px) translateY(150px) scale(0.5);
    opacity: 0;
  }
}

@keyframes spin-left {
  0% {
    transform: rotate(0deg) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateX(500px) rotate(1080deg) scale(0.3);
    opacity: 0;
  }
}

@keyframes spin-right {
  0% {
    transform: rotate(0deg) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateX(-500px) rotate(1080deg) scale(0.3);
    opacity: 0;
  }
}

@keyframes pop {
  0% {
    transform: scale(0.7);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
